import { useState } from "react";
import { Check, ChevronDown, Minus } from "lucide-react";
import { cn } from "~/lib/utils";
import { PLAN_COMPARISON_ROWS } from "../../consts";

// // // //

const PLAN_COMPARISON_COLUMN_NAMES = ["Public", "Preview", "Pro", "Enterprise"];

export const COMPARE_PLANS_SECTION_ID = "compare-plans";

export function ComparePlansTable() {
    const [mobilePlan, setMobilePlan] = useState(2);

    return (
        <section id={COMPARE_PLANS_SECTION_ID} className="mt-16 scroll-mt-28">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2
                        id="comparison-heading"
                        className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
                    >
                        Compare every plan
                    </h2>
                </div>
            </div>
            <div className="mb-5 flex items-center justify-between gap-3 text-base sm:hidden">
                <label htmlFor="comparison-plan" className="font-medium">
                    View plan
                </label>
                <div className="inline-grid grid-cols-[1fr_--spacing(8)]">
                    <select
                        id="comparison-plan"
                        name="comparison-plan"
                        value={mobilePlan}
                        onChange={(event) =>
                            setMobilePlan(Number(event.target.value))
                        }
                        className="col-span-full row-start-1 min-h-12 appearance-none rounded-md border border-foreground/15 bg-card py-2 pr-8 pl-3 focus-visible:outline-2 focus-visible:outline-ring"
                    >
                        {PLAN_COMPARISON_COLUMN_NAMES.map((name, index) => (
                            <option key={name} value={index}>
                                {name}
                            </option>
                        ))}
                    </select>
                    <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none col-start-2 row-start-1 size-4 place-self-center"
                    />
                </div>
            </div>
            <div
                role="region"
                aria-label="Plan comparison"
                tabIndex={0}
                className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 focus-visible:outline-2 focus-visible:outline-ring"
            >
                <div className="inline-block min-w-full px-4 py-2 align-middle sm:px-6">
                    <table
                        id="comparison-table"
                        className="w-full border-collapse text-left text-base tabular-nums sm:text-sm"
                    >
                        <caption className="sr-only">
                            Public, Preview, Pro, and Enterprise feature
                            comparison. Not included means the plan does not
                            provide access.
                        </caption>
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="w-1/2 whitespace-nowrap py-5 pr-4 align-bottom font-medium text-muted-foreground sm:w-auto sm:pr-6"
                                >
                                    Features & limits
                                </th>
                                {PLAN_COMPARISON_COLUMN_NAMES.map(
                                    (name, index) => (
                                        <th
                                            key={name}
                                            scope="col"
                                            className={cn(
                                                "px-3 py-5 align-bottom font-semibold sm:min-w-36 sm:whitespace-nowrap",
                                                {
                                                    "bg-primary/5":
                                                        name === "Pro",
                                                    "max-sm:hidden":
                                                        mobilePlan !== index,
                                                },
                                            )}
                                        >
                                            {name === "Pro" && (
                                                <div className="mb-2">
                                                    <span className="inline-block rounded border border-primary-dark/10 bg-primary-light px-2 py-0.5 text-xs font-normal text-primary-dark">
                                                        Recommended
                                                    </span>
                                                </div>
                                            )}
                                            <span className="text-xl font-semibold tracking-tight text-foreground">
                                                {name}
                                            </span>
                                        </th>
                                    ),
                                )}
                            </tr>
                        </thead>
                        {PLAN_COMPARISON_ROWS.map((group) => (
                            <tbody key={group.title}>
                                {group.title && (
                                    <tr>
                                        <th
                                            colSpan={5}
                                            scope="rowgroup"
                                            className="border-y border-foreground/10 py-3 text-left font-semibold sm:whitespace-nowrap"
                                        >
                                            {group.title}
                                        </th>
                                    </tr>
                                )}
                                {group.rows.map((row) => (
                                    <tr
                                        key={row.feature}
                                        className="border-b border-foreground/10"
                                    >
                                        <th
                                            scope="row"
                                            className="py-4 pr-4 align-top font-normal sm:whitespace-nowrap sm:pr-6"
                                        >
                                            {row.feature}
                                            {row.description && (
                                                <p className="mt-1 max-w-56 whitespace-normal text-xs text-muted-foreground">
                                                    {row.description}
                                                </p>
                                            )}
                                        </th>
                                        {row.values.map((value, index) => (
                                            <td
                                                key={index}
                                                className={cn(
                                                    "px-3 py-4 align-top",
                                                    {
                                                        "max-sm:hidden":
                                                            mobilePlan !==
                                                            index,
                                                        "bg-primary/5":
                                                            index === 2,
                                                        "text-muted-foreground":
                                                            value === false,
                                                    },
                                                )}
                                            >
                                                {value === true ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Check
                                                            className="size-4 h-lh shrink-0 stroke-primary"
                                                            aria-hidden="true"
                                                        />{" "}
                                                        Included
                                                    </span>
                                                ) : value === false ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Minus
                                                            className="size-4 h-lh shrink-0"
                                                            aria-hidden="true"
                                                        />{" "}
                                                        Not included
                                                    </span>
                                                ) : (
                                                    value
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        ))}
                    </table>
                </div>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
                * Subject to anti-abuse limitations.
            </p>
        </section>
    );
}
